import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ConditionMonitoringService,
  IDisplayVehicle,
  IPositionData,
  ISelectedPosition,
} from '../../../condition-monitoring.service';
import {
  ICON_WIDTH,
  ICON_HEIGHT,
} from '../../condition-monitoring-details.component';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { XlatPipe } from '@amt/i18n/xlat-pipe';
import OcConfigSvc from '@Services/ocConfigSvc';
import { take, takeUntil } from 'rxjs/operators';
import {
  IVisualInspectionsResult,
} from '@Interfaces/visualInspection.interfaces';
import {
  IColumns,
  IDataConfig,
} from '@Modules/components/custom-grid/custom-grid.component';
import { IOcVehicleSchematicConfig } from '@Interfaces/visualInspection.interfaces';
import { IPosition, IPositionFitment, IVisualInspectionBasic, IVisualInspectionRawData, IVisualInspectionRecord } from '@Models/condition-monitoring/IVisualInspection';
import { OPEN_VISUAL_INSPECTION_STATUS } from '@OC-common/utils/condition-monitoring-visual-inspections.utils';

const NULL_GUID = '00000000-0000-0000-0000-000000000000';

@Component({
  selector: 'visual-inspections',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './visual-inspections.component.html',
  styleUrls: ['./visual-inspections.component.scss'],
  providers: [
    { provide: 'desiredWidth', useValue: ICON_WIDTH },
    { provide: 'desiredHeight', useValue: ICON_HEIGHT },
    ConditionMonitoringService,
  ],
})
export class VisualInspectionsComponent implements OnInit, OnDestroy {
  @Input() vehicle!: IDisplayVehicle;
  @Input() currentVehicle$!: Observable<any>;
  @Input() currentVehicle?: any;
  isProcessing: boolean = false;
  arrPositions: IPosition[] = [];
  selectedComponent: ISelectedPosition = null;

  public vehicleFitmentData: IPositionFitment[];
  private vehicleVisualInspectionsData: IVisualInspectionBasic[];
  public selectedTyre$ = new BehaviorSubject<string>(undefined);
  public vehicleModel$ = new BehaviorSubject<any>(undefined);
  public latestVisualInspection$ = new BehaviorSubject<IVisualInspectionBasic>(undefined);
  public earliestDueVisualInspection$ = new BehaviorSubject<IVisualInspectionBasic>(undefined);
  public displayVisualInspections$ = new BehaviorSubject<IVisualInspectionRecord[]>(undefined);

  @Output() updateSelectedComponent = new EventEmitter<ISelectedPosition>();

  private cachedDisplayVisualInspections: {[key:string]: IVisualInspectionRecord[]} = {};
  private onDestroy$ = new Subject<void>();

  ocVehicleSchematicConfig: IOcVehicleSchematicConfig = {
    maxWidth: 300,
    maxHeight: 350,
    leftPos: 1,
    topAxle: 1,
  };

  dataGridConfig: IDataConfig;
  columns: IColumns[] = [
    {
      title: this.xlatPipe.transform('visualInspection.lastUpdated'),
      field: 'inspection_date',
      isUrl: true,
    },
    {
      title: this.xlatPipe.transform('UI.Resource.Common.Pos'),
      field: 'fitmentPos',
    },
    {
      title: this.xlatPipe.transform('visualInspection.cause'),
      field: 'cause',
    },
    {
      title: this.xlatPipe.transform('visualInspection.action'),
      field: 'action',
    },
    {
      title: this.xlatPipe.transform('conditionmonitoring.Action_Due_Date_Column'),
      field: 'action_due_date',
    },
    {
      title: this.xlatPipe.transform('visualInspection.images'),
      field: 'attachments',
    },
    {
      title: this.xlatPipe.transform('visualInspectionAction.active_grid'),
      field: 'active',
    },
  ];
  @Output() finishedProcessing = new EventEmitter<boolean>();
  constructor(
    public monSvc: ConditionMonitoringService,
    private xlatPipe: XlatPipe,
    private ocConfigSvc: OcConfigSvc,
    private ref: ChangeDetectorRef,
    @Inject('WindowFactory') private WindowFactory: any
  ) {}
  @HostBinding('style.--img-w') public readonly cssImgWidth = ICON_WIDTH;
  @HostBinding('style.--img-h') public readonly cssImgHeight = ICON_HEIGHT;
  ngOnInit(): void {
    this.isProcessing = true;
    
    // vehicle VI data
    this.vehicleModel$.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe((vehicle) => {
      if (!vehicle) return;
      this.doneProcessing();
      const fitment: IPositionFitment[] = [];
      this.vehicleVisualInspectionsData = this.transformVisualInspectionBasic(vehicle, fitment);
      this.vehicleFitmentData = fitment;
      this.arrPositions = ConditionMonitoringService.getArrPositions(vehicle);
      this.selectedTyre$.next(undefined);
      this.ref.detectChanges();
    });
    if (this.currentVehicle) {
      this.vehicleModel$.next(this.currentVehicle);
    } else {
      this.currentVehicle$.subscribe((x) => this.vehicleModel$.next(x));
    };

    // current VI view onChange
    this.selectedTyre$.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe((equipmentId) => {
      const visualInspections = (!!equipmentId
        ? this.vehicleVisualInspectionsData.filter(e => e.equipmentId === equipmentId)
        : this.vehicleVisualInspectionsData)?.filter(e => e.active);
      const openStatus = visualInspections?.some((vi) => OPEN_VISUAL_INSPECTION_STATUS.some((status) => status === vi.action));
      this.populateVisualInspectionRecords(equipmentId || this.vehicle.equipmentId, !equipmentId);
      let lastVIcheck: IVisualInspectionBasic = undefined;
      let nextVIcheck: IVisualInspectionBasic = undefined;
      if (visualInspections?.length > 0) {
        lastVIcheck = visualInspections.sort((a,b) => b.lastVisualInspectionDate.getTime() - a.lastVisualInspectionDate.getTime())[0];
        nextVIcheck = openStatus
          ? this.getSeverityAction(visualInspections)
          : visualInspections.sort((a,b) => a.nextActionDueDate.getTime() - b.nextActionDueDate.getTime())[0];

      }
      this.earliestDueVisualInspection$.next(nextVIcheck);
      this.latestVisualInspection$.next(lastVIcheck);
      this.ref.markForCheck();
    })
    combineLatest([
      this.earliestDueVisualInspection$,
      this.latestVisualInspection$,
    ]).pipe(
      takeUntil(this.onDestroy$),
    ).subscribe((_) => {
      this.ref.markForCheck();
    });
  }

  private transformVisualInspectionBasic(vehicle: any, positionFitment: IPositionFitment[] = []): IVisualInspectionBasic[] {
    const result = [];
    for (let i = 0; i < vehicle.visualInspectionDetails?.length; i++) {
      const viDetail = vehicle.visualInspectionDetails[i];
      if (viDetail.visualInspectionItems && viDetail.visualInspectionItems.length > 0) {
        for (let j = 0; j < viDetail.visualInspectionItems.length; j++) {
          /* Filling an array for the vehicle schematic */
          const { active, visualInspectionRecord } = viDetail.visualInspectionItems[j];
          const dateBasis = new Date(visualInspectionRecord.date);
          const openStatus = OPEN_VISUAL_INSPECTION_STATUS.some((status) => status === visualInspectionRecord.action);
          const visualInspectionRecordObj: IVisualInspectionBasic = {
            id: visualInspectionRecord.id,
            lastVisualInspectionDate: dateBasis,
            lastVisualInspectionBy: visualInspectionRecord.inspectedBy,
            actionDaysToResolve: visualInspectionRecord.actionDaysToResolve,
            nextActionDueDate: openStatus ? undefined : new Date(
              this.monSvc.getActionDueDate(
                dateBasis,
                visualInspectionRecord.actionDaysToResolve,
              )
            ),
            equipmentId: viDetail.equipmentId,
            action: visualInspectionRecord.action,
            active,
          };
          result.push(visualInspectionRecordObj);
        }
        positionFitment.push({
          equipmentId: viDetail.equipmentId,
          active: viDetail.visualInspectionItems.some(({ active }) => active),
          action: this.getSeverityAction(
            viDetail.visualInspectionItems.map(({visualInspectionRecord})=> visualInspectionRecord),
          )?.action,
        })
      }
    }
    return result;
  }

  private populateVisualInspectionRecords(equipmentId: string, isVehicle = true) {
    if (!equipmentId || this.isProcessing) return;
    const key = `${equipmentId}_${isVehicle ? 'A' : 'B'}@`;
    const viRecords = this.cachedDisplayVisualInspections[key];
    if (!viRecords) {
      this.monSvc.getVisualInspectionRecords(equipmentId, isVehicle)
        .pipe(
          take(1),
          takeUntil(this.onDestroy$),
        ).subscribe((visualInspections) => {
          const result = [];
          for (let i = 0; i < visualInspections.length; i++) {
            const { visualInspections: vis, positionId, index } = visualInspections[i];
            for(let j = 0; j< vis.length; j++) {
              const viData = vis[j];
              const position = this.arrPositions.find((x) => x.equipmentId === (positionId || viData.equipmentId));
              const positionData = !!position ? position : this.arrPositions.find((x) => x.position === `${index + 1}`);
              const objData = this.transformToVisualInspectionRecord(viData, positionData);
              result.push(objData);
            }
          }
          const key = `${equipmentId}_${isVehicle ? 'A' : 'B'}@`;
          this.cachedDisplayVisualInspections[key] = result;
          this.displayVisualInspections$.next(result);
          this.doneProcessing();
        });
    } else {
      this.displayVisualInspections$.next(viRecords);
    }
  }

  getSeverityAction(items: IVisualInspectionBasic[]): IVisualInspectionBasic {
    // ordered by severity -> index 0 is the most severe
    const severityPriority = [
      'VisualInspectionAction.Schedule for Removal in Next 24 Hours',
      'VisualInspectionAction.Schedule for Removal in Next 7 Days',
      'VisualInspectionAction.Schedule for Removal in Next Month',
      'VisualInspectionAction.Schedule for Removal in Next Service',
      'VisualInspectionAction.Continue to Monitor',
    ];
    let result = 0;
    for (let i = 1; i < items?.length; i++) {
      const { active, action } = items[i];  
      if (active) {
        const severity = severityPriority.indexOf(action);
        const resultSeverity = severityPriority.indexOf(items[result].action);
        result = (severity >= 0 && resultSeverity > severity) ? i : result;
      }
    }
    return items && items[result];
  }

  showComponent(event: any) {
    if (this.selectedComponent == null) {
      this.updateSelectedComponent.emit()
    }
    this.monSvc.showComponent(event.dataItem);
  }

  viCount(active: boolean) {
    if (active) {
      const yes = this.xlatPipe.transform('common.yes_label');
      return this.displayVisualInspections$.value?.filter((vi) => vi.active === yes).length || 0;
    }
    return this.selectedTyre$.value
      ? this.vehicleVisualInspectionsData?.filter(vi => vi.equipmentId === this.selectedTyre$.value).length || 0
      : this.vehicleVisualInspectionsData?.length || 0;
  }

  getVIStatus(visualInspection: IVisualInspectionBasic): string | undefined {
    if (!visualInspection) return;
    const dateToCheck = visualInspection.nextActionDueDate;
    const today = new Date();

    if (!dateToCheck || dateToCheck > today) {
      return 'conditionmonitoring.vi_statustype';
    } else {
      return 'conditionmonitoring.vi_overdue';
    }
  }

  private doneProcessing() {
    this.finishedProcessing.emit(true);
    this.isProcessing = false;
  }

  private transformToVisualInspectionRecord(viData: IVisualInspectionsResult, positionData: IPosition): IVisualInspectionRecord {
  const { siteId, serialNumber, position } = positionData;
  return {
    equipmentId: viData.equipmentId !== NULL_GUID ? viData.equipmentId : positionData.equipmentId,
    siteId: siteId,
    serialNumber: serialNumber,
    date: viData.inspectionDateTime,
    fitmentPos: position,
    cause: viData.cause,
    action: viData.action,
    action_due_date: !viData.active || viData.actionDueDate && viData.inspectionDateTime.getTime() - viData.actionDueDate.getTime() === 0
      ? undefined
      : viData.actionDueDate,
    attachments: viData.attachments,
    active: viData.active ? this.xlatPipe.transform('common.yes_label') : this.xlatPipe.transform('common.no_label'),
  };
}

  tyreSelected(tyre: IPositionData) {
    if (
      tyre.component.type !== 'Tyre' ||
      !tyre.component.equipmentId ||
      tyre.component.equipmentId.length === 0
    )
      return; 

    const currentSelectedTyre = this.selectedTyre$.value;
    const { equipmentId } = tyre.component
    if (!currentSelectedTyre || currentSelectedTyre !== equipmentId) {
      this.selectedTyre$.next(equipmentId);
    } else {
      this.selectedTyre$.next(undefined);
    }
    this.onSelectedComponentChanged(tyre);
  }

  onSelectedComponentChanged(tyre: IPositionData) {
    //If it is the currently selected tyre then unselect it? How does that work with the ocschematic...
    if (
      this.selectedComponent !== null &&
      this.selectedComponent.equipmentId == tyre.component.equipmentId
    ) {
      this.selectedComponent = null;
    } else {
      this.selectedComponent = tyre.component;

      this.selectedComponent.fittedVehicle = this.vehicle.serialNo;
      this.selectedComponent.percentWorn =
        (
          (1 -
            tyre.component.remainingDepth.value /
              tyre.component.primaryOTD.value) *
          100
        )
          .toFixed(this.ocConfigSvc.general.percentageWornDecimals)
          .toString() + '%';
      this.selectedComponent.positionLabel = tyre.position;
    }

    this.updateSelectedComponent.emit(this.selectedComponent);
  }

  showVehicle() {
    this.monSvc.showVehicle(this.vehicle, this.vehicleModel$.value);
  }

  showSelectedTyre() {
    this.monSvc.showComponent(this.selectedComponent);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.onDestroy$.unsubscribe();
  }
}
